import { translate } from "app/language/service"
import { NOT_SAVED_ID } from "app/model"
import { getAllCampaignLanguages } from "campaign/model"
import { FieldSelectAdvertiserDomain } from "form/components/fields/FieldSelectAdvertiserDomain"
import { FieldSelectDsp } from "form/components/fields/FieldSelectDsp"
import { IForm } from "form/model"
import { canChangeCampaignOrder } from "form/tools/configuration"
import React from "react"
import { FlexBox } from "ui/components/FlexBox"
import { FieldDate } from "../../fields/FieldDate"
import { FieldImpressionCount } from "../../fields/FieldImpressionCount"
import { FieldSelect } from "../../fields/FieldSelect"
import { FieldSelectOrder } from "../../fields/FieldSelectOrder"
import { FieldTags } from "../../fields/FieldTags"
import { FieldText } from "../../fields/FieldText"
import { OrderEditField } from "./OrderEditField"

interface IConfigurationMainFormProps {
  form: IForm
}

const END_DATE_FIELD_NAME: string = "main.endDate"

export const ConfigurationMainForm = React.memo(({ form }: IConfigurationMainFormProps): JSX.Element => {
  const {
    context,
    statusId,
    configuration: { main, dspDeal }
  } = form
  const mainErrors = context.configuration.errors.main
  const dspDealErrors = context.configuration.errors.dspDeal
  const isForecastForm = form.context.isForecast
  const isDspDealForm = form.context.isDspDeal
  return (
    <FlexBox
      ai="start"
      jc="space-between"
      wrap
      gap
      fullWidth
      sx={{
        "height": "100%",
        "minHeight": "100%",
        "maxHeight": "100%",
        "p": "1rem",
        "& > *": {
          width: "20rem"
        }
      }}>
      <FlexBox vertical gap sx={{ "& > *": { width: "100%" } }}>
        <FieldText
          fieldName="main.name"
          label={translate(isForecastForm ? "forecast.name" : isDspDealForm ? "dsp-deal.name" : "campaign.name")}
          value={main.name}
          error={mainErrors?.name}
          required
        />
        {isDspDealForm && (
          <FieldText
            fieldName="dspDeal.dealid"
            label={"Dealid"}
            value={dspDeal.dealid}
            error={dspDealErrors?.dealid}
            required
          />
        )}
        {!isForecastForm ? (
          <FieldSelectOrder
            orderFieldName="main.order_id"
            isDspDeal={isDspDealForm}
            advertiserFieldName="main.advertiser_id"
            disabled={!canChangeCampaignOrder(statusId)}
            error={mainErrors?.order_id}
            sx={{ width: "20rem" }}
            sxFirstField={{
              "justifyContent": "start",
              "width": "100%",
              "flexWrap": "nowrap",
              "& > div:first-of-type": {
                width: "100%"
              },
              "& > button": { width: "10rem" }
            }}
            sxSecondField={{ width: "100%" }}
          />
        ) : (
          <FieldTags disabled={form.tempCampaignId === NOT_SAVED_ID} userTagIds={form.configuration.main.userTagIds} />
        )}
        {isForecastForm && (
          <FieldText
            fieldName="main.description"
            textarea
            label={translate("label.description")}
            value={main.description}
            error={mainErrors?.description}
            disabled={form.tempCampaignId === NOT_SAVED_ID}
          />
        )}
        {isDspDealForm && (
          <FieldText
            fieldName="dspDeal.description"
            textarea
            label={translate("label.description")}
            value={dspDeal.description}
            error={dspDealErrors?.description}
          />
        )}
      </FlexBox>
      <FlexBox vertical gap sx={{ "& > *": { width: "100%" } }}>
        {!isForecastForm && <OrderEditField form={form} mainForm={main} toDisplayInfo="impressions" />}
        <FieldImpressionCount
          inputValue={main.max_views}
          checkboxValue={main.unlimitedImpressions}
          error={mainErrors?.max_views}
        />
      </FlexBox>
      <FlexBox vertical gap sx={{ "& > *": { width: "100%" } }}>
        <FieldDate
          fieldName="main.startDate"
          label={translate("label.startDate")}
          value={main.startDate}
          error={mainErrors?.startDate}
          required
          minTime
          toTouchFieldName={END_DATE_FIELD_NAME}
        />
        <FieldDate
          fieldName={END_DATE_FIELD_NAME}
          label={translate("label.endDate")}
          value={main.endDate}
          error={mainErrors?.endDate}
          maxTime
          required
        />
        <FieldSelect
          fieldName="main.language_id"
          label={translate("label.language")}
          value={main.language_id}
          options={getAllCampaignLanguages()}
          optionLabelFn={locale => translate(`label.language.${locale}`)}
          error={mainErrors?.language_id}
        />
        {isDspDealForm && (
          <>
            <FieldSelectDsp fieldName="dspDeal.dsp_id" required error={dspDealErrors?.dsp_id} />
            <FieldSelectAdvertiserDomain
              disabled={form.tempCampaignId === NOT_SAVED_ID}
              fieldName="dspDeal.advertiserDomainIds"
            />
          </>
        )}
      </FlexBox>
    </FlexBox>
  )
})
